import type { SavedViewState } from '@/features/saved-views/lib/saved-views-client.js';
import { ApiError } from '@/lib/api/errors.js';
import { useAuth } from '@/lib/auth/use-auth.js';
import { t } from '@/lib/i18n/messages.js';
import type { ColumnDef, ParameterDef, ViewState } from '@quartup/reports-shared';
import { ReportTemplateModelSchema } from '@quartup/reports-shared';
import { useState } from 'react';
import { useExecute } from '../hooks/use-execute.js';
import { useExport } from '../hooks/use-export.js';
import { useReportDetail } from '../hooks/use-report-detail.js';
import { AppliedQueryPanel } from './applied-query-panel.js';
import { ChartPanel } from './chart-panel.js';
import { ExecuteBar } from './execute-bar.js';
import { ExportModal } from './export-modal.js';
import type { ParamValues } from './params-form.js';
import { ParamsForm } from './params-form.js';
import { ReportHeader } from './report-header.js';
import { ResultsErrorState } from './results-error-state.js';
import { ResultsTable } from './results-table.js';

interface ReportDetailPageProps {
  tenantReportId: string;
}

function LoadingState() {
  return (
    <div
      className="flex items-center justify-center py-20"
      aria-busy="true"
      aria-label={t('report.loading')}
    >
      <svg
        className="h-8 w-8 animate-spin text-[var(--color-qup-primary)]"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="sr-only">{t('report.loading')}</span>
    </div>
  );
}

function DetailLoadError({ error }: { error: Error }) {
  const isNotFound = error instanceof ApiError && error.isNotFound;
  return (
    <div role="alert" className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-base font-semibold text-[var(--color-qup-text-dark)]">
        {isNotFound ? t('report.not_found') : t('report.error')}
      </p>
      <p className="mt-1 text-sm text-[var(--color-qup-text-muted-dark)]">{error.message}</p>
    </div>
  );
}

export function ReportDetailPage({ tenantReportId }: ReportDetailPageProps) {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;

  const { data: detail, isLoading, error: detailError } = useReportDetail(tenantReportId);
  const executeMutation = useExecute(tenantReportId);
  const exportHook = useExport(tenantReportId);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [paramValues, setParamValues] = useState<ParamValues>({});
  // Tracks the last applied view_state (columns, sort, etc.) — updated on every execute/load.
  // Initialized to undefined so we can fall back to defaultView on first render.
  const [appliedViewState, setAppliedViewState] = useState<Record<string, unknown> | undefined>(
    undefined,
  );

  if (isLoading) return <LoadingState />;
  if (detailError !== null && detailError !== undefined)
    return <DetailLoadError error={detailError} />;
  if (detail === undefined) return null;

  // Parse definition into typed template
  const templateParsed = ReportTemplateModelSchema.safeParse(detail.definition);
  const template = templateParsed.success ? templateParsed.data : null;

  const parameters: ParameterDef[] =
    template !== null && 'parameters' in template ? (template.parameters as ParameterDef[]) : [];

  const columns: ColumnDef[] =
    template !== null && 'columns' in template ? (template.columns as ColumnDef[]) : [];

  const defaultView =
    template !== null && 'default_view' in template
      ? (template.default_view as ViewState | undefined)
      : undefined;

  const visualization = defaultView?.visualization;

  // Map server PARAM_VALIDATION errors back to individual field errors
  const paramErrors =
    executeMutation.error instanceof ApiError && executeMutation.error.code === 'PARAM_VALIDATION'
      ? extractParamErrors(executeMutation.error)
      : [];

  function extractParamErrors(err: ApiError): Array<{ paramName: string; message: string }> {
    const details = err.details as Record<string, unknown> | undefined;
    if (details === undefined) return [];
    const fields = details.fields as Record<string, string> | undefined;
    if (fields === undefined) return [];
    return Object.entries(fields).map(([name, msg]) => ({ paramName: name, message: msg }));
  }

  async function handleExecute(values: ParamValues, fresh: boolean) {
    setParamValues(values);
    // Use the last applied view_state if available; fall back to the template default.
    const viewState = appliedViewState ?? (defaultView as Record<string, unknown> | undefined);
    setAppliedViewState(viewState);
    await executeMutation.mutateAsync({
      params: values as Record<string, unknown>,
      ...(viewState !== undefined ? { view_state: viewState } : {}),
      fresh,
    });
  }

  async function handleFormSubmit(values: ParamValues) {
    await handleExecute(values, false);
  }

  function handleLoadView(state: SavedViewState) {
    // Apply saved view state: merge params and re-execute
    const loadedParams = state.params as ParamValues;
    setParamValues(loadedParams);
    setAppliedViewState(state.view);
    void executeMutation.mutateAsync({
      params: state.params,
      view_state: state.view,
      fresh: false,
    });
  }

  const executeResult = executeMutation.data;
  const executeError = executeMutation.error;
  const isExecuting = executeMutation.isPending;

  // currentViewState reflects the last applied state (source of truth for saving views).
  // Falls back to template default_view if user has not yet executed.
  const currentViewState: SavedViewState = {
    params: paramValues as Record<string, unknown>,
    view: appliedViewState ?? (defaultView as Record<string, unknown> | undefined) ?? {},
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <ReportHeader
        detail={detail}
        currentState={currentViewState}
        onLoadView={handleLoadView}
        {...(detail.capabilities.canExport ? { onExport: () => setExportModalOpen(true) } : {})}
      />

      {/* Params form */}
      {parameters.length > 0 && (
        <ParamsForm
          parameters={parameters}
          disabled={isExecuting}
          paramErrors={paramErrors}
          onSubmit={handleFormSubmit}
        />
      )}

      {/* Execute bar */}
      <ExecuteBar
        isExecuting={isExecuting}
        {...(executeResult !== undefined
          ? {
              durationMs: executeResult.meta.durationMs,
              cacheHit: executeResult.meta.cacheHit,
              rowCount: executeResult.totalRows,
            }
          : {})}
        onExecute={(fresh) => {
          const current = Object.keys(paramValues).length > 0 ? paramValues : {};
          void handleExecute(current, fresh);
        }}
        canExport={detail.capabilities.canExport}
        onExport={() => setExportModalOpen(true)}
      />

      {/* Error state */}
      {executeError !== null &&
        executeError !== undefined &&
        !(executeError instanceof ApiError && executeError.code === 'PARAM_VALIDATION') && (
          <ResultsErrorState error={executeError} />
        )}

      {/* Chart panel */}
      {executeResult !== undefined &&
        visualization !== undefined &&
        visualization !== 'table' &&
        columns.length > 0 &&
        (() => {
          const categoryKey = columns[0]?.key;
          const valueKey = columns.find((c) => c.type === 'money' || c.type === 'number')?.key;
          return (
            <ChartPanel
              rows={executeResult.rows}
              visualization={visualization}
              {...(categoryKey !== undefined ? { categoryKey } : {})}
              {...(valueKey !== undefined ? { valueKey } : {})}
            />
          );
        })()}

      {/* Results table */}
      {executeResult !== undefined && (
        <ResultsTable
          columns={columns.length > 0 ? columns : buildColumnsFromRows(executeResult.rows)}
          rows={executeResult.rows}
          totalRows={executeResult.totalRows}
        />
      )}

      {/* Applied query (admin only) */}
      {isAdmin && executeResult?.appliedQuery !== undefined && (
        <AppliedQueryPanel query={executeResult.appliedQuery} />
      )}

      {/* Export modal */}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        reportCode={detail.code}
        currentParams={paramValues}
        currentViewState={appliedViewState ?? (defaultView as Record<string, unknown> | undefined)}
        exportState={exportHook.state}
        startExport={exportHook.startExport}
        cancelExport={exportHook.cancel}
        resetExport={exportHook.reset}
      />
    </div>
  );
}

function buildColumnsFromRows(rows: ReadonlyArray<Record<string, unknown>>): ColumnDef[] {
  if (rows.length === 0) return [];
  const first = rows[0];
  if (first === undefined) return [];
  return Object.keys(first).map((key) => ({
    key,
    type: 'string' as const,
    label: { es: key },
    sortable: true,
  }));
}
